import {
  type QuizBuildQuestionStepFailReason,
  type QuizBuildQuestionType,
} from '@/shared/quiz/events.quiz-build';

import { type BuildQuizListItem } from '../form/schema/BuildQuizListSchema';
import { MATCH_ITEM_CONTENT_REQUIRED_MESSAGE } from '../form/schema/quiz/QuizMatchDataSchema';
import { CHOICE_CONTENT_REQUIRED_MESSAGE } from '../form/schema/quiz/QuizMultipleDataSchema';
import { ORDER_ITEM_CONTENT_REQUIRED_MESSAGE } from '../form/schema/quiz/QuizOrderDataSchema';
import { ANSWER_TEXT_REQUIRED_MESSAGE } from '../form/schema/quiz/QuizSubjectiveDataSchema';

import {
  type EmptyAnswerInputSkipReason,
  type getEmptyAnswerInputCleanups,
} from './getEmptyAnswerInputCleanups';
import { type QuizListValidationResult } from './useValidateQuizList';

const EMPTY_ITEM_MESSAGES = new Set([
  ANSWER_TEXT_REQUIRED_MESSAGE,
  CHOICE_CONTENT_REQUIRED_MESSAGE,
  ORDER_ITEM_CONTENT_REQUIRED_MESSAGE,
  MATCH_ITEM_CONTENT_REQUIRED_MESSAGE,
]);

const SKIP_FAIL_REASON: Record<
  EmptyAnswerInputSkipReason,
  QuizBuildQuestionStepFailReason
> = {
  below_min: 'below_min_item',
  answer_choice_empty: 'answer_choice_empty',
  match_half_filled: 'match_half_filled',
};

const getFailReason = (
  validation: Exclude<QuizListValidationResult, { isValid: true }>,
  skips: ReturnType<typeof getEmptyAnswerInputCleanups>['skips'],
): QuizBuildQuestionStepFailReason => {
  if (validation.reason !== 'invalid_quiz') return validation.reason;
  if (
    validation.errorPath?.[0] === 'question' &&
    validation.errorCode === 'too_small'
  ) {
    return 'question_empty';
  }
  if (
    !validation.errorMessage ||
    !EMPTY_ITEM_MESSAGES.has(validation.errorMessage)
  ) {
    return 'other';
  }

  const skip = skips.find(
    ({ quizIndex }) => quizIndex === validation.quizIndex,
  );
  return skip ? SKIP_FAIL_REASON[skip.reason] : 'empty_item';
};

const countQuizzes = (
  quizList: BuildQuizListItem[],
  type: QuizBuildQuestionType,
) => quizList.filter(quiz => quiz.type === type).length;

export const getQuestionStepSubmitProps = ({
  quizList,
  cleanupResult: { cleanups, skips },
  validation,
}: {
  quizList: BuildQuizListItem[];
  cleanupResult: ReturnType<typeof getEmptyAnswerInputCleanups>;
  validation: QuizListValidationResult;
}) => {
  const cleanedCount = (type: QuizBuildQuestionType) =>
    cleanups.filter(({ quizType }) => quizType === type).length;
  const failure = validation.isValid
    ? {}
    : {
        fail_reason: getFailReason(validation, skips),
        ...(validation.reason === 'invalid_quiz' &&
        quizList[validation.quizIndex]
          ? { fail_question_type: quizList[validation.quizIndex].type }
          : {}),
      };

  return {
    result: validation.isValid
      ? ('success' as const)
      : ('validation_fail' as const),
    ...failure,
    has_match_question: countQuizzes(quizList, 'match') > 0,
    match_question_count: countQuizzes(quizList, 'match'),
    has_order_question: countQuizzes(quizList, 'order') > 0,
    order_question_count: countQuizzes(quizList, 'order'),
    cleaned_subjective_question_count: cleanedCount('subjective'),
    cleaned_multiple_question_count: cleanedCount('multiple'),
    cleaned_order_question_count: cleanedCount('order'),
    cleaned_match_question_count: cleanedCount('match'),
    removed_item_count: cleanups.reduce(
      (sum, { removedCount }) => sum + removedCount,
      0,
    ),
  };
};
